import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
} from "@tanstack/react-query";
import axios from "axios";

import {
  awsAccountsApi,
  entitiesApi,
  eventsApi,
  featureQueriesApi,
  featuresApi,
  workspacesApi,
} from "src/api/endpoints";
import { userOrganizationsQueryKey, workspaceKeys } from "src/api/queries";
import {
  accessRequestsApi,
  organizationsApi,
  rolesApi,
  usersApi,
} from "src/api/taktileApi";
import {
  EntityResource,
  ListSchemasEntitiesApiV1SchemasSchemaGetSchemaEnum,
} from "src/clients/entities";
import { AWSAccount, WorkspaceDataplane } from "src/clients/flow-api";
import { ServiceInfraMetadata } from "src/clients/router";
import {
  AccessRequestCreatePayload,
  AccessRequestPage,
  AccessRequestStatus,
  OrganizationCreate,
  OrganizationInDB,
  OrganizationName,
  RequestableRole,
  RoleCreate,
  RoleInDB,
  User,
} from "src/clients/taktile-api";
import { getInfoEndpoint } from "src/powertools/utils";
import { queryClient } from "src/queryClient";

// Type for the paginated user response
interface UserPageResponse {
  users: User[];
  total: number;
  skip: number;
  limit: number;
  has_more: boolean;
}

export const useInfoEndpoints = (
  workspaces: WorkspaceDataplane[],
  enabled: boolean,
) => {
  return useQueries({
    queries: workspaces.map((workspace) => ({
      queryKey: ["wsinfo", workspace.id] as const,
      queryFn: async () =>
        (await axios.get(getInfoEndpoint(workspace)))
          .data as ServiceInfraMetadata,
      enabled: enabled,
      staleTime: 1 * 60 * 1000,
      retry: 1, // Only retry once to avoid hammering failed workspaces
    })),
  });
};

export const usePatchWorkspace = () => {
  return useMutation({
    mutationKey: ["patchWorkspace"],
    mutationFn: (workspace: WorkspaceDataplane) => {
      return workspacesApi.updateWorkspaceApiV1WorkspacesWorkspaceIdPatch(
        workspace.id,
        {
          ...(workspace.config && { config: workspace.config }),
          ...(workspace.feature_gates && {
            feature_gates: workspace.feature_gates,
          }),
        },
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: workspaceKeys.all,
      });
    },
  });
};

export const useCreateOrganization = () => {
  return useMutation({
    mutationKey: ["createOrganization"],
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userOrganizationsQueryKey });
    },
    mutationFn: ({
      name,
      principal,
    }: Pick<OrganizationCreate, "name" | "principal">) => {
      return organizationsApi.addOrganizationApiV1OrganizationsPost({
        name,
        principal,
        allows_signup: false,
        allows_decide_access: true,
        whitelabel: false,
      });
    },
  });
};

export const useUpdateUser = () => {
  return useMutation({
    mutationKey: ["updateUsers"],
    mutationFn: (user: User) => {
      return usersApi.updateUserApiV1UsersIdUserIdPut(user.id, user);
    },
  });
};

export const useCreateRole = () => {
  return useMutation({
    mutationKey: ["createRole"],
    onMutate: async (newRole: RoleCreate) => {
      await queryClient.cancelQueries({ queryKey: ["allUsers"] });
      const previousUsers = queryClient.getQueryData<User[]>(["allUsers"]);
      const tempRoleId = `temp-${Date.now()}`;

      queryClient.setQueryData<User[]>(
        ["allUsers"],
        (old: User[] | undefined) => {
          if (!old) return old;
          return old.map((user: User) =>
            user.id === newRole.user_id
              ? {
                  ...user,
                  roles: [
                    ...(user.roles || []),
                    {
                      id: tempRoleId,
                      name: newRole.name,
                      arguments: newRole.arguments,
                      user_id: newRole.user_id,
                      created_at: new Date().toISOString(),
                      updated_at: new Date().toISOString(),
                    },
                  ],
                }
              : user,
          );
        },
      );

      return { previousUsers, tempRoleId };
    },
    onError: (
      _err: unknown,
      _newRole: RoleCreate,
      context:
        | { previousUsers: User[] | undefined; tempRoleId: string }
        | undefined,
    ) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(["allUsers"], context.previousUsers);
      }
    },
    onSuccess: (
      data: { data: RoleInDB },
      variables: RoleCreate,
      context: { previousUsers: User[] | undefined; tempRoleId: string },
    ) => {
      const isSameRole = (role: RoleInDB): boolean =>
        role.name === data.data.name &&
        role.arguments?.org_id === data.data.arguments?.org_id;

      const replaceTemporaryRole = (role: RoleInDB): RoleInDB =>
        role.id === context.tempRoleId && isSameRole(role) ? data.data : role;

      const updateUserRoles = (user: User): User =>
        user.id === variables.user_id
          ? { ...user, roles: user.roles?.map(replaceTemporaryRole) }
          : user;

      queryClient.setQueryData<User[]>(
        ["allUsers"],
        (old: User[] | undefined) => {
          if (!old) return old;
          return old.map(updateUserRoles);
        },
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["usersByRole"] });
    },
    mutationFn: (roleCreate: RoleCreate) =>
      rolesApi.createRoleApiV1RolesPost(roleCreate),
  });
};

export const useDeleteRole = () => {
  return useMutation({
    mutationKey: ["deleteRole"],
    onMutate: async (roleId: string) => {
      await queryClient.cancelQueries({ queryKey: ["allUsers"] });
      const previousUsers = queryClient.getQueryData<User[]>(["allUsers"]);

      const removeRoleFromUser = (user: User): User => ({
        ...user,
        roles: user.roles?.filter((role: RoleInDB) => role.id !== roleId),
      });

      queryClient.setQueryData<User[]>(
        ["allUsers"],
        (old: User[] | undefined) => {
          if (!old) return old;
          return old.map(removeRoleFromUser);
        },
      );

      return { previousUsers };
    },
    onError: (
      _err: unknown,
      _roleId: string,
      context: { previousUsers: User[] | undefined } | undefined,
    ) => {
      if (context?.previousUsers) {
        queryClient.setQueryData(["allUsers"], context.previousUsers);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["usersByRole"] });
    },
    mutationFn: (roleId: string) =>
      rolesApi.deleteRoleApiV1RolesRoleIdDelete(roleId),
  });
};

export const useAllUsers = () => {
  return useQuery<User[], Error>({
    queryKey: ["allUsers"],
    queryFn: async () => {
      let skip = 0;
      const limit = 100;
      let result: User[] = [];

      while (true) {
        const response = await usersApi.getUsersApiV1UsersGet(skip, limit);
        result = result.concat(response.data);
        if (response.data.length < limit) {
          break;
        }
        skip += limit;
      }

      return result;
    },
  });
};

export const useAllOrgs = () => {
  return useQuery<OrganizationInDB[], Error>({
    queryKey: ["allOrgs"],
    queryFn: async () => {
      let skip = 0;
      const limit = 30;
      let result: OrganizationInDB[] = [];

      while (true) {
        const response =
          await organizationsApi.getOrganizationsApiV1OrganizationsAllGet(
            skip,
            limit,
          );
        result = result.concat(response.data);
        if (response.data.length < limit) {
          break;
        }
        skip += limit;
      }
      return result;
    },
  });
};

const ORG_NAMES_PAGE_SIZE = 30;

const fetchAllOrgNames = async (
  skip: number,
  acc: OrganizationName[],
): Promise<OrganizationName[]> => {
  const response =
    await organizationsApi.getOrganizationNamesApiV1OrganizationsNamesGet(
      skip,
      ORG_NAMES_PAGE_SIZE,
    );
  const collected = acc.concat(response.data);
  return response.data.length < ORG_NAMES_PAGE_SIZE
    ? collected
    : fetchAllOrgNames(skip + ORG_NAMES_PAGE_SIZE, collected);
};

export const useAllOrgNames = () => {
  return useQuery<OrganizationName[], Error>({
    queryKey: ["allOrgNames"],
    queryFn: () => fetchAllOrgNames(0, []),
  });
};

export const useWorkspaceEventTypes = (
  workspace: WorkspaceDataplane | null,
) => {
  return useQuery({
    queryKey: ["workspaceEventTypes", workspace?.id],
    queryFn: async () => {
      const api = eventsApi(`${workspace?.base_url}`);
      const response = await api.getAllEventConfigsEventsGet();
      return response.data;
    },
    enabled: !!workspace,
  });
};

export const useWorkspaceFeatures = (workspace: WorkspaceDataplane | null) => {
  return useQuery({
    queryKey: ["workspaceFeatures", workspace?.id],
    queryFn: async () => {
      const api = featuresApi(`${workspace?.base_url}`);
      const response = await api.getFeaturesFeaturesGet();
      return response.data;
    },
    enabled: !!workspace,
  });
};

export const useWorkspaceFeatureQueries = (
  workspace: WorkspaceDataplane | null,
) => {
  return useQuery({
    queryKey: ["workspaceFeatureQueries", workspace?.id],
    queryFn: async () => {
      const api = featureQueriesApi(`${workspace?.base_url}`);
      const response = await api.getFeatureQueriesFeatureQueriesGet();
      return response.data;
    },
    enabled: !!workspace,
  });
};

export const useWorkspaceEntitySchemas = (
  workspace: WorkspaceDataplane | null,
) => {
  return useQuery({
    queryKey: ["workspaceEntitySchemas", workspace?.id],
    queryFn: async () => {
      const api = entitiesApi(`${workspace?.base_url}`);
      const response = await api.listSchemasEntitiesApiV1SchemasSchemaGet({
        schema: ListSchemasEntitiesApiV1SchemasSchemaGetSchemaEnum.ENTITY,
        limit: 100,
      });
      return response.data.entities as EntityResource[];
    },
    enabled: !!workspace,
  });
};

export const useAwsAccountInfo = (accountId: string) => {
  return useQuery({
    queryKey: ["awsAccountInfo", accountId],
    queryFn: async () => {
      const response =
        await awsAccountsApi.getAwsAccountApiV1AwsAccountsAccountIdGet(
          accountId,
        );
      return response.data;
    },
    enabled: !!accountId,
  });
};

export const useAllAWSAccounts = (includeDisabled = false) => {
  return useQuery<AWSAccount[], Error>({
    queryKey: ["awsAccounts"],
    queryFn: async () => {
      const response = await awsAccountsApi.getAwsAccountsApiV1AwsAccountsGet(
        0,
        100,
        includeDisabled,
      );
      return response.data;
    },
  });
};

export const useUpdateAWSAccount = () => {
  return useMutation({
    mutationKey: ["awsAccounts"],
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["awsAccounts"] });
    },
    mutationFn: (account: AWSAccount) => {
      return awsAccountsApi.updateAwsAccountApiV1AwsAccountsAccountIdPatch(
        account.id,
        account,
      );
    },
  });
};

export const useCreateAWSAccount = () => {
  return useMutation({
    mutationKey: ["awsAccounts"],
    mutationFn: (account: AWSAccount) => {
      return awsAccountsApi.createAwsAccountApiV1AwsAccountsPost(account);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["awsAccounts"] });
      queryClient.invalidateQueries({ queryKey: ["awsAccountInfo"] });
    },
  });
};

export const useRequestableRoles = () => {
  return useQuery<RequestableRole[], Error>({
    queryKey: ["requestableRoles"],
    queryFn: async () => {
      const response =
        await accessRequestsApi.getRequestableRolesApiV1AdminAccessRequestsRequestableRolesGet();
      return response.data;
    },
  });
};

export type AccessRequestFilters = {
  status?: AccessRequestStatus;
  organizationId?: string;
  skip?: number;
  limit?: number;
};

export const useAccessRequests = (filters: AccessRequestFilters = {}) => {
  const { status, organizationId, skip = 0, limit = 20 } = filters;
  return useQuery<AccessRequestPage, Error>({
    queryKey: ["accessRequests", "all", status, organizationId, skip, limit],
    queryFn: async () => {
      const response =
        await accessRequestsApi.getAccessRequestsApiV1AdminAccessRequestsAllGet(
          status,
          organizationId,
          skip,
          limit,
        );
      return response.data;
    },
  });
};

export const useMyAccessRequests = (filters: AccessRequestFilters = {}) => {
  const { status, organizationId, skip = 0, limit = 20 } = filters;
  return useQuery<AccessRequestPage, Error>({
    queryKey: ["accessRequests", "my", status, organizationId, skip, limit],
    queryFn: async () => {
      const response =
        await accessRequestsApi.getMyAccessRequestsApiV1AdminAccessRequestsGet(
          status,
          organizationId,
          skip,
          limit,
        );
      return response.data;
    },
  });
};

export const useCreateAccessRequest = () => {
  return useMutation({
    mutationKey: ["createAccessRequest"],
    mutationFn: (payload: AccessRequestCreatePayload) =>
      accessRequestsApi.createAccessRequestApiV1AdminAccessRequestsPost(
        payload,
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["accessRequests"] });
    },
  });
};

export const useUpdateAccessRequestStatus = () => {
  return useMutation({
    mutationKey: ["updateAccessRequestStatus"],
    mutationFn: ({
      accessRequestId,
      status,
      decisionReason,
      accessDurationDays,
    }: {
      accessRequestId: string;
      status: AccessRequestStatus;
      decisionReason?: string;
      accessDurationDays?: number;
    }) =>
      accessRequestsApi.updateAccessRequestStatusApiV1AdminAccessRequestsAccessRequestIdPatch(
        accessRequestId,
        {
          status,
          decision_reason: decisionReason,
          access_duration_days: accessDurationDays,
        },
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["accessRequests"] });
    },
  });
};

export const useUsersByRole = (roleName: string, pageSize = 50) => {
  return useInfiniteQuery({
    queryKey: ["usersByRole", roleName, pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      const response = await usersApi.getUserByRoleApiV1UsersRoleRoleNameGet(
        roleName,
        pageParam,
        pageSize,
      );

      // Handle both old and new API response formats
      const data = response.data;
      if (Array.isArray(data)) {
        // Old format - convert to new format
        return {
          users: data,
          total: data.length,
          skip: pageParam,
          limit: pageSize,
          has_more: false,
        } as UserPageResponse;
      } else {
        // New format
        return data as UserPageResponse;
      }
    },
    getNextPageParam: (lastPage: UserPageResponse) => {
      if (!lastPage.has_more) {
        return undefined;
      }
      return lastPage.skip + lastPage.limit;
    },
    initialPageParam: 0,
    enabled: !!roleName,
  });
};
